import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Stack, TextField, Typography,
} from '@mui/material';
import WorkflowService, { WorkflowInstance, WorkflowInstanceStep } from '../../api/services/workflow.service';
import { DAY_LABEL, SCHEDULE_DAYS, ScheduleDay } from '../../api/services/schedule.service';
import DayCell from '../schedule/DayCell';

// The step a workflow is waiting on, answered from the web as on the phone:
// pick a choice (with a note), confirm a check, or fill the step's form.
// Photo-proof steps stay on the phone, where the camera is.
//
// The one form today is a driver's work days and capacity, asked when
// their training is over; the run carries a suggestion to start from.

type Schedule = { days: ScheduleDay[]; maxStops: number; maxPackages: number };

const StepAnswer: React.FC<{
  inst: WorkflowInstance;
  step: WorkflowInstanceStep;
  onDone: (inst: WorkflowInstance) => void;
}> = ({ inst, step, onDone }) => {
  const suggested = inst.context?.suggestedSchedule as Schedule | undefined;
  const [notes, setNotes] = useState('');
  const [schedule, setSchedule] = useState<Schedule>(suggested || { days: ['mon', 'tue', 'wed', 'thu', 'fri'], maxStops: 0, maxPackages: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setNotes(''); setError(null); if (suggested) setSchedule(suggested); }, [step.stepId, suggested]);

  if (step.proofType === 'photo') {
    return <Alert severity="info" sx={{ mb: 2 }}>This step needs a photo; answer it on the phone.</Alert>;
  }

  const answer = async (contextPatch?: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      onDone(await WorkflowService.completeStep(inst._id, step.stepId, { notes: notes.trim() || undefined, ...(contextPatch ? { contextPatch } : {}) }));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'That answer was not saved.');
    } finally {
      setBusy(false);
    }
  };
  const toggle = (d: ScheduleDay) => setSchedule((s) => ({
    ...s, days: s.days.includes(d) ? s.days.filter((x) => x !== d) : SCHEDULE_DAYS.filter((x) => x === d || s.days.includes(x)),
  }));
  const num = (v: string) => Math.max(0, Math.round(Number(v) || 0));

  return (
    <Card sx={{ p: 2, mb: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
      <Typography variant="overline" color="text.secondary">Waiting on you</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{step.title}</Typography>
      {step.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{step.description}</Typography>}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {step.form === 'driver_schedule' ? (
        <Stack gap={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">Work days</Typography>
            <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }}>
              {SCHEDULE_DAYS.map((d) => (
                <Box key={d} sx={{ width: 52, textAlign: 'center' }}>
                  <Typography variant="caption">{DAY_LABEL[d]}</Typography>
                  <DayCell on={schedule.days.includes(d)} label={DAY_LABEL[d]} onToggle={() => toggle(d)} disabled={busy} />
                </Box>
              ))}
            </Stack>
          </Box>
          <Stack direction="row" gap={1.5}>
            <TextField size="small" type="number" label="Stops a day" value={schedule.maxStops} onChange={(e) => setSchedule((s) => ({ ...s, maxStops: num(e.target.value) }))} sx={{ width: 140 }} disabled={busy} />
            <TextField size="small" type="number" label="Packages a day" value={schedule.maxPackages} onChange={(e) => setSchedule((s) => ({ ...s, maxPackages: num(e.target.value) }))} sx={{ width: 160 }} disabled={busy} />
          </Stack>
          {suggested && (
            <Typography variant="caption" color="text.secondary">
              Suggested from their work areas and the company&apos;s usual capacity ({suggested.maxStops} stops, {suggested.maxPackages} packages). Change anything that is not right.
            </Typography>
          )}
          <Box>
            <Button variant="contained" disableElevation disabled={busy || !schedule.days.length} onClick={() => answer({ schedule })}>
              Save their schedule
            </Button>
          </Box>
        </Stack>
      ) : (
        <Stack gap={1.5}>
          <TextField size="small" multiline minRows={2} label="Note" placeholder="Optional" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy} inputProps={{ maxLength: 2000 }} />
          <Stack direction="row" gap={1} flexWrap="wrap">
            {step.choices?.length
              ? step.choices.map((c, i) => (
                <Button key={c.label} variant={i === 0 ? 'contained' : 'outlined'} disableElevation disabled={busy} onClick={() => answer(c.contextPatch)}>{c.label}</Button>
              ))
              : <Button variant="contained" disableElevation disabled={busy} onClick={() => answer()}>Done</Button>}
          </Stack>
        </Stack>
      )}
    </Card>
  );
};

export default StepAnswer;
